import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha, makeStyles, useTheme } from '@material-ui/core/styles';
import { Tooltip } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';

/**
 * @constant CIRCLE_SIZE
 * @description Diameter, in px, of a step's numbered circle. Small enough that the whole indicator
 * stays a slim strip above the step controls rather than a block of its own.
 * @memberof module:SHARED
 */
const CIRCLE_SIZE = 32;

/**
 * @constant CONNECTOR_THICKNESS
 * @description Thickness, in px, of the line drawn between two steps.
 * @memberof module:SHARED
 */
const CONNECTOR_THICKNESS = 1;

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Builds the stepper's own look from
 * the theme's text/divider colors, leaving the accent (the module's identity color) to be injected
 * per instance so the same component can head a green module and an orange one without a second
 * implementation.
 * The connector is drawn as a `::before` on every step but the first, spanning from the previous
 * circle's center to this one's, which keeps each step a single self-contained column - no
 * separate connector elements to keep in sync with the step count.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:SHARED
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  list: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    width: '100%',
    maxWidth: 560,
    margin: '0 auto',
    padding: 0,
    listStyle: 'none',
  },
  step: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    '&::before': {
      content: '""',
      position: 'absolute',
      top: (CIRCLE_SIZE - CONNECTOR_THICKNESS) / 2,
      right: `calc(50% + ${CIRCLE_SIZE / 2 + 8}px)`,
      left: `calc(-50% + ${CIRCLE_SIZE / 2 + 8}px)`,
      height: CONNECTOR_THICKNESS,
      backgroundColor: theme.palette.divider,
      borderRadius: CONNECTOR_THICKNESS,
    },
    '&:first-child::before': {
      display: 'none',
    },
  },
  stepReached: {
    '&::before': {
      backgroundColor: 'var(--module-primary)',
    },
  },
  stepDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  /**
   * Carries `aria-disabled` rather than `disabled`: a disabled button emits no pointer or focus
   * events, so the tooltip explaining why the step is closed would never open, and keyboard users
   * could not reach the explanation at all. The click is refused in the handler instead.
   */
  trigger: {
    appearance: 'none',
    border: 0,
    margin: 0,
    padding: 0,
    background: 'none',
    font: 'inherit',
    color: 'inherit',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    borderRadius: 6,
    '&:focus-visible': {
      outline: `2px solid var(--module-primary)`,
      outlineOffset: 3,
    },
    '&[aria-disabled="true"]': {
      cursor: 'not-allowed',
    },
  },
  circle: {
    position: 'relative',
    zIndex: 1,
    boxSizing: 'border-box',
    width: CIRCLE_SIZE,
    height: CIRCLE_SIZE,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 1,
    border: `1.5px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
  },
  circleActive: {
    borderColor: 'var(--module-primary)',
    backgroundColor: 'var(--module-primary)',
    color: 'var(--module-primary-contrast)',
  },
  circleDone: {
    borderColor: 'var(--module-primary)',
    backgroundColor: 'var(--module-primary-soft)',
    color: 'var(--module-primary)',
  },
  /**
   * A step the flow is going to pass over - Bathymetry's area step, when a preset area makes it
   * unnecessary. Struck through, in the theme's warning color, so it reads as "not going to
   * happen" rather than as done or as still ahead, and it keeps its number instead of taking a
   * check it never earned.
   */
  circleSkipped: {
    borderColor: theme.palette.warning.main,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.warning.main,
  },
  checkIcon: {
    fontSize: 18,
  },
  label: {
    margin: 0,
    textAlign: 'center',
    fontSize: 13,
    lineHeight: 1.3,
    letterSpacing: 0.1,
    color: theme.palette.text.secondary,
    hyphens: 'auto',
  },
  labelActive: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  labelDone: {
    color: theme.palette.text.primary,
  },
  labelSkipped: {
    color: theme.palette.warning.main,
    textDecoration: 'line-through',
  },
}));

/**
 * @function WorkflowStepper
 * @description Horizontal progress indicator for a linear, multi-step flow: a numbered circle per
 * step, its label underneath, and a line joining them. Purely presentational - it derives every
 * state from `currentStep`'s position in `steps` and never advances anything itself, which is what
 * lets it sit on top of a flow whose transitions are driven from inside the steps themselves.
 * Steps before the current one read as done (a check), the current one is filled with the module's
 * accent color, and later ones stay grey. The connector leading into a reached step takes the
 * accent too, so the colored run of the line is itself a progress bar.
 * The accent is applied through CSS custom properties set on the root element rather than written
 * into each rule, so one declaration re-colors the whole indicator and a second module only has to
 * pass its own `accentColor`.
 * Renders as an ordered list and marks the current step with `aria-current="step"`, so the order
 * and the position in it survive without the visuals. State is never carried by color alone: a
 * done step swaps its number for a check and the current one's label is the only one in bold.
 * @param {Object} props
 * @param {Array<{id: String, label: String, disabled: Boolean, skipped: Boolean}>} props.steps -
 *  Steps to show, in order. `disabled` refuses the click on a step that cannot be opened yet;
 *  `skipped` marks one the flow is going to pass over.
 * @param {String} props.currentStep - `id` of the step in progress. A value that is not in `steps`
 *  (a terminal state the indicator does not display, say) leaves every step done - see `allDone`.
 * @param {Boolean} [props.allDone=false] - Marks every step as concluded, for when the flow has
 *  finished and no step is current any more.
 * @param {String} [props.accentColor] - The module's identity color. Defaults to the closest
 *  theme's primary, which each module's own `ThemeProvider` already sets.
 * @param {String} [props.className] - Optional extra class for the wrapping element.
 * @param {Function} [props.onStepClick] - `(index, step) => void`. Given, each step becomes a real
 *  `<button>`: reachable by keyboard, with its own focus ring. Left out, the steps are plain text
 *  and nothing about them suggests they can be clicked - which is the right reading for a flow
 *  that only ever advances from inside its own steps.
 * @param {String} [props.disabledHint] - Why a closed step cannot be opened, shown on hover and on
 *  focus. Defaults to a generic "finish the earlier steps" line. A closed step that simply does
 *  nothing when clicked reads as a broken control, which is the one thing worse than a locked one.
 * **Called by:
 * @see {AcquisitionPage} - every module's acquisition wizard, above its steps.
 * @memberof module:SHARED
 * @returns {JSX.Element}
 */
const WorkflowStepper = ({
  steps,
  currentStep,
  allDone = false,
  accentColor,
  className,
  onStepClick,
  disabledHint,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const [t] = useTranslation();
  const blockedHint = disabledHint || t('forms.core.workflowStepper.blocked');
  const primary = accentColor || theme.palette.primary.main;
  const currentIndex = steps.findIndex((step) => step.id === currentStep);

  return (
    <ol
      className={[classes.list, className].filter(Boolean).join(' ')}
      style={{
        '--module-primary': primary,
        '--module-primary-hover': theme.palette.type === 'dark' ? alpha(primary, 0.8) : alpha(primary, 0.85),
        '--module-primary-soft': alpha(primary, 0.12),
        '--module-primary-contrast': theme.palette.getContrastText(primary),
      }}>
      {steps.map((step, index) => {
        const isDone = allDone || (currentIndex !== -1 && index < currentIndex);
        const isActive = !allDone && index === currentIndex;
        const isUpcoming = !isDone && !isActive;
        const isDisabled = Boolean(step.disabled);
        const isSkipped = Boolean(step.skipped);

        const content = (
          <>
            <span
              className={[
                classes.circle,
                isActive && classes.circleActive,
                isDone && !isSkipped && classes.circleDone,
                isSkipped && classes.circleSkipped,
              ]
                .filter(Boolean)
                .join(' ')}>
              {isDone && !isSkipped ? <CheckIcon className={classes.checkIcon} aria-hidden="true" /> : index + 1}
            </span>
            <p
              className={[
                classes.label,
                isActive && classes.labelActive,
                isDone && classes.labelDone,
                isSkipped && classes.labelSkipped,
              ]
                .filter(Boolean)
                .join(' ')}>
              {step.label}
            </p>
          </>
        );

        return (
          <li
            key={step.id}
            aria-current={isActive ? 'step' : undefined}
            aria-disabled={!onStepClick && isUpcoming ? true : undefined}
            className={[
              classes.step,
              (isDone || isActive) && classes.stepReached,
              !isActive && (isUpcoming || isDisabled) && classes.stepDisabled,
            ]
              .filter(Boolean)
              .join(' ')}>
            {onStepClick ? (
              <Tooltip title={isDisabled ? blockedHint : ''} placement="bottom">
                <button
                  type="button"
                  className={classes.trigger}
                  aria-disabled={isDisabled || undefined}
                  onClick={() => !isDisabled && onStepClick(index, step)}>
                  {content}
                </button>
              </Tooltip>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default WorkflowStepper;
